import i18n from '@/i18n'
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAgency } from "@/contexts/AgencyContext";
import { getAgencyLogoUrl } from "@/utils/agencyLogo";
import { supabase } from "@/lib/supabase";
import AuthPageBackground from "@/components/ui/shared/AuthPageBackground";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { agency } = useAgency();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const logoSrc = getAgencyLogoUrl(agency, true, "/logo-white.png");

  useEffect(() => {
    const validateRecoveryToken = async () => {
      try {
        if (!supabase) {
          setError(t("reset.notConfigured"));
          setValidatingToken(false);
          return;
        }

        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !data.session) {
          console.error("Token de recuperação inválido:", sessionError);
          setError(t("reset.invalidLink"));
          setValidatingToken(false);
          return;
        }

        console.log("✅ Token de recuperação válido");
        setValidatingToken(false);
      } catch (err) {
        console.error("Erro ao validar token:", err);
        setError(t("reset.validateError"));
        setValidatingToken(false);
      }
    };

    validateRecoveryToken();
  }, [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError(t("reset.fillAllFields"));
      return;
    }

    if (password.length < 6) {
      setError(t("reset.tooShort"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("reset.mismatch"));
      return;
    }

    setLoading(true);

    try {
      if (!supabase) {
        setError(i18n.t('common:errors.notConfigured'));
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error("Erro ao atualizar senha:", updateError);
        setError(t("reset.resetError"));
        return;
      }

      console.log("✅ Senha atualizada com sucesso");
      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      console.error("Erro ao redefinir senha:", err);
      setError(i18n.t('auth:errors.resetPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <AuthPageBackground />
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50 text-lg">{t("reset.validatingLink")}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <AuthPageBackground />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={logoSrc}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || t("common.logoAlt")}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-md">
              <p className="text-green-300 text-sm text-center">
                {t("reset.successTitle")}
              </p>
              <p className="text-neutral-300 text-xs text-center mt-2">
                {t("reset.successBody")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && validatingToken === false && !password) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <AuthPageBackground />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={logoSrc}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || t("common.logoAlt")}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm text-center">{error}</p>
            </div>
            <Button onClick={() => navigate("/login")} variant="primary">
              {t("common.backToLogin")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex justify-center items-center relative">
      <AuthPageBackground />
      <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
        <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
          <img
            src={logoSrc}
            className="h-[57px] max-w-[180px] self-center object-contain"
            alt={agency?.company_name || t("common.logoAlt")}
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
          <div>
            <h2 className="text-xl font-semibold mb-2">
              {t("reset.title")}
            </h2>
            <p className="text-sm text-neutral-300">
              {t("reset.description")}
            </p>
          </div>
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="password" className="text-sm">
                {t("reset.newPassword")}
              </label>
              <Input
                type="password"
                id="password"
                placeholder={t("reset.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="confirmPassword" className="text-sm">
                {t("reset.confirmPassword")}
              </label>
              <Input
                type="password"
                id="confirmPassword"
                placeholder={t("reset.passwordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="mt-4" disabled={loading}>
              {loading ? t("reset.submitting") : t("reset.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
